/**
 * Blog AJAX pagination + entry animation.
 *
 * Pagination: intercepts pagination clicks inside [data-blog-grid], fetches the
 * target page, extracts [data-blog-grid-inner] from the response, and swaps it in
 * without a full page reload. URL updates via history.pushState so that
 * direct links and the browser back/forward buttons still work.
 *
 * Animation: the heading (breadcrumbs + H1 + text) fades in immediately since it's
 * always above the fold; listing items reveal on scroll. GSAP is lazy-loaded only
 * when motion is allowed.
 */

class BlogGridPagination {
	constructor(grid) {
		this.grid = grid;

		// Persist current URL in state so popstate can restore it.
		history.replaceState({ blogPaged: true, url: location.href }, '');

		this.grid.addEventListener('click', (e) => {
			const link = e.target.closest('.page-numbers a');
			if (!link) return;
			e.preventDefault();
			this.navigate(link.href);
		});

		window.addEventListener('popstate', (e) => {
			if (e.state?.blogPaged) {
				this.fetchAndSwap(e.state.url, false);
			}
		});
	}

	navigate(url) {
		history.pushState({ blogPaged: true, url }, '', url);
		this.fetchAndSwap(url, true);
	}

	fetchAndSwap(url, scrollToGrid) {
		this.grid.setAttribute('data-loading', '');

		fetch(url)
			.then((res) => res.text())
			.then((html) => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const newInner = doc.querySelector('[data-blog-grid-inner]');
				const currentInner = this.grid.querySelector('[data-blog-grid-inner]');

				if (newInner && currentInner) {
					currentInner.replaceWith(newInner);
				}

				if (scrollToGrid) {
					this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			})
			.catch((err) => {
				console.error('Blog pagination fetch failed:', err);
			})
			.finally(() => {
				this.grid.removeAttribute('data-loading');
			});
	}
}

const blogGrid = document.querySelector('[data-blog-grid]');

if (blogGrid) {
	new BlogGridPagination(blogGrid);
}

const blogHeading = document.querySelector('.blog-heading');
const listingItems = document.querySelectorAll('.blog-listing__item');

if (blogHeading || listingItems.length) {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!reduceMotion) {
		listingItems.forEach((item) => {
			if (item.getBoundingClientRect().bottom > 0) {
				item.dataset.animate = '';
			}
		});

		(async () => {
			const { gsap } = await import('gsap');
			const { ScrollTrigger } = await import('gsap/ScrollTrigger');

			gsap.registerPlugin(ScrollTrigger);

			if (blogHeading) {
				const breadcrumbs = blogHeading.querySelector('.blog-heading__breadcrumbs');
				const heading = blogHeading.querySelector('.blog-heading__heading');
				const text = blogHeading.querySelector('.blog-heading__text');
				const headerEls = [breadcrumbs, heading, text].filter(Boolean);

				gsap.context(() => {
					gsap.set(headerEls, { opacity: 0, y: 20 });

					gsap.to(headerEls, {
						opacity: 1,
						y: 0,
						duration: 0.8,
						ease: 'power3.out',
						stagger: 0.1,
					});
				}, blogHeading);
			}

			const animatedItems = document.querySelectorAll('.blog-listing__item[data-animate]');

			if (animatedItems.length) {
				gsap.to(animatedItems, {
					opacity: 1,
					y: 0,
					duration: 0.6,
					ease: 'power3.out',
					stagger: 0.1,
					scrollTrigger: {
						trigger: animatedItems[0].closest('.blog-listing__list'),
						start: 'top 90%',
						once: true,
					},
				});
			}
		})();
	}
}
